import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { EventItem } from "@/types";
import StatusBadge from "@/components/base/StatusBadge";
import { formatDateShort, getWeekday } from "@/utils/format";

interface HeroCarouselProps {
  slides: EventItem[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides.length]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/events?q=${encodeURIComponent(q)}` : "/events");
  };

  if (slides.length === 0) return null;
  const active = slides[index];

  return (
    <section className="relative w-full h-[460px] md:h-[600px] overflow-hidden bg-secondary-900">
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img src={s.cover} alt={s.title} className="w-full h-full object-cover object-top" />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/90 via-secondary-950/55 to-secondary-950/40" />

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex flex-col justify-center">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-label tracking-wide">
              今日焦点
            </span>
            <StatusBadge status={active.status} />
            <span className="text-xs text-background-200">
              {formatDateShort(active.date)} {getWeekday(active.date)} · {active.time}
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-background-50 leading-tight">
            {active.title}
          </h2>
          <p className="mt-4 text-sm md:text-base text-background-200 max-w-2xl leading-relaxed line-clamp-2">
            {active.summary}
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to={`/event/${active.id}`}
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看比赛详情 <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/schedule"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md border border-background-50/60 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-line" /> 查看完整赛程
            </Link>
          </div>
        </div>

        <form
          onSubmit={submitSearch}
          className="mt-8 md:mt-10 w-full max-w-xl flex items-center gap-2 p-1.5 rounded-full bg-background-50/95 backdrop-blur border border-background-200"
        >
          <i className="ri-search-line text-foreground-400 text-lg ml-3" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索赛事名称、参赛队伍或选手"
            className="flex-1 h-9 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="submit"
            className="h-9 px-5 rounded-full bg-secondary-900 text-background-50 text-sm font-medium hover:bg-secondary-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 z-10 flex items-center gap-3">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => setIndex((i) => (i - 1 + slides.length) % slides.length)}
          className="w-9 h-9 rounded-full border border-background-50/50 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <div className="flex items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? "w-6 bg-primary-500" : "w-1.5 bg-background-50/60"}`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => setIndex((i) => (i + 1) % slides.length)}
          className="w-9 h-9 rounded-full border border-background-50/50 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>
    </section>
  );
}